import React, { useState } from "react";
import { PREMIUM_LIVE } from "../config/plan";
import { useAuth, db } from "../context/AuthContext";
import {
  exportUserData,
  deleteLinkedRecords,
  downloadJson,
} from "../utils/userData";
import { openConsentSettings } from "../utils/consent";

const font = '"DM Sans", sans-serif';

const card = {
  border: "1px solid #ede9fe",
  borderRadius: 14,
  padding: "1rem 1.1rem",
  marginBottom: "0.9rem",
  background: "#fff",
};
const h = { margin: "0 0 0.3rem", fontSize: "0.98rem", fontWeight: 700, color: "#1e1b4b" };
const p = { margin: "0 0 0.7rem", fontSize: "0.84rem", lineHeight: 1.55, color: "#4b5563" };
const btn = (variant) => ({
  padding: "0.65rem 1.1rem",
  borderRadius: 10,
  fontFamily: font,
  fontWeight: 700,
  fontSize: "0.85rem",
  cursor: "pointer",
  border: variant === "ghost" ? "2px solid #7c3aed" : "none",
  background:
    variant === "danger" ? "#dc2626" : variant === "ghost" ? "#fff" : "#7c3aed",
  color: variant === "ghost" ? "#7c3aed" : "#fff",
});

const PrivacyDataModal = ({ onClose }) => {
  const { user, deleteAccount } = useAuth();
  const [busy, setBusy] = useState("");
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");
  const [confirming, setConfirming] = useState(false);
  const [typed, setTyped] = useState("");
  const [password, setPassword] = useState("");
  const [deleted, setDeleted] = useState(false);

  const usesPassword = (user?.providerData || []).some(
    (x) => x.providerId === "password",
  );

  const reset = () => {
    setNotice("");
    setError("");
  };

  const handleExport = async () => {
    reset();
    setBusy("export");
    try {
      const data = await exportUserData(db, user);
      downloadJson("noryq-my-data.json", data);
      setNotice("Your data file has been downloaded.");
    } catch (e) {
      console.error(e);
      setError("Could not prepare your data. Please try again or email us.");
    } finally {
      setBusy("");
    }
  };

  const handleWaitlist = async () => {
    reset();
    setBusy("waitlist");
    try {
      const n = await deleteLinkedRecords(db, user.uid, ["waitlist"]);
      setNotice(
        n > 0
          ? "You have been removed from the Premium waitlist."
          : "No waitlist entry was found for your account. If you joined while signed out, email us and we will remove it.",
      );
    } catch (e) {
      console.error(e);
      setError("Could not update the waitlist. Please try again or email us.");
    } finally {
      setBusy("");
    }
  };

  const handleDelete = async () => {
    reset();
    setBusy("delete");
    try {
      await deleteAccount(password);
      setDeleted(true);
      setTimeout(() => {
        window.location.href = "/";
      }, 2500);
    } catch (e) {
      setError(e.message || "Something went wrong. Please try again.");
    } finally {
      setBusy("");
    }
  };

  const canDelete =
    typed.trim().toUpperCase() === "DELETE" && (!usesPassword || password);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Privacy and data"
      onClick={(e) => e.target === e.currentTarget && !busy && onClose()}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 3000,
        background: "rgba(15,12,41,0.6)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "1rem",
        fontFamily: font,
      }}
    >
      <div
        style={{
          width: "100%",
          maxWidth: 520,
          maxHeight: "92vh",
          overflowY: "auto",
          background: "#faf8ff",
          borderRadius: 20,
          padding: "1.25rem",
          boxShadow: "0 20px 60px rgba(0,0,0,0.35)",
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.9rem" }}>
          <h2 style={{ margin: 0, fontSize: "1.25rem", fontWeight: 800, color: "#1e1b4b" }}>
            Privacy &amp; Data
          </h2>
          {!busy && (
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              style={{ background: "none", border: "none", fontSize: 24, cursor: "pointer", color: "#6b7280" }}
            >
              ×
            </button>
          )}
        </div>

        {deleted ? (
          <div style={{ ...card, textAlign: "center" }}>
            <h3 style={h}>Your account has been deleted</h3>
            <p style={{ ...p, marginBottom: 0 }}>
              Your profile, lists, history and sign-in have been removed. Taking
              you back to the home page...
            </p>
          </div>
        ) : (
          <>
            {notice && (
              <div role="status" style={{ ...card, background: "#ecfdf5", borderColor: "#a7f3d0", color: "#065f46", fontSize: "0.85rem" }}>
                {notice}
              </div>
            )}
            {error && (
              <div role="alert" style={{ ...card, background: "#fef2f2", borderColor: "#fecaca", color: "#991b1b", fontSize: "0.85rem" }}>
                {error}
              </div>
            )}

            <div style={card}>
              <h3 style={h}>Download my data</h3>
              <p style={p}>
                Get a copy of your account details, saved lists, history,
                feedback and waitlist entries as a file.
              </p>
              <button type="button" style={btn()} disabled={!!busy} onClick={handleExport}>
                {busy === "export" ? "Preparing..." : "Download my data"}
              </button>
            </div>

            <div style={card}>
              <h3 style={h}>Cookie &amp; tracking choices</h3>
              <p style={p}>Change what analytics and advertising you allow, any time.</p>
              <button
                type="button"
                style={btn("ghost")}
                onClick={() => {
                  onClose();
                  setTimeout(openConsentSettings, 50);
                }}
              >
                Open cookie settings
              </button>
            </div>

            <div style={card}>
              <h3 style={h}>Premium waitlist</h3>
              <p style={p}>
                Withdraw your consent to be emailed about the Premium launch and
                delete your waitlist entry.
              </p>
              <button type="button" style={btn("ghost")} disabled={!!busy} onClick={handleWaitlist}>
                {busy === "waitlist" ? "Removing..." : "Remove me from the waitlist"}
              </button>
            </div>

            <div style={{ ...card, borderColor: "#fecaca" }}>
              <h3 style={{ ...h, color: "#b91c1c" }}>Delete my account</h3>
              <p style={p}>
                This permanently deletes your profile, saved lists, watch
                history, feedback, waitlist entries and your sign-in. It cannot
                be undone.
                {PREMIUM_LIVE &&
                  " Records of any payments you made are kept for the period the law requires."}
              </p>
              {!confirming ? (
                <button type="button" style={btn("danger")} onClick={() => setConfirming(true)}>
                  Delete my account...
                </button>
              ) : (
                <div>
                  <label style={{ ...p, display: "block", marginBottom: "0.4rem" }}>
                    Type <strong>DELETE</strong> to confirm
                  </label>
                  <input
                    value={typed}
                    onChange={(e) => setTyped(e.target.value)}
                    autoComplete="off"
                    aria-label="Type DELETE to confirm"
                    style={{ width: "100%", padding: "0.6rem", borderRadius: 8, border: "1px solid #d1d5db", marginBottom: "0.6rem", fontFamily: font, boxSizing: "border-box" }}
                  />
                  {usesPassword ? (
                    <input
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Your password"
                      autoComplete="current-password"
                      aria-label="Your password"
                      style={{ width: "100%", padding: "0.6rem", borderRadius: 8, border: "1px solid #d1d5db", marginBottom: "0.6rem", fontFamily: font, boxSizing: "border-box" }}
                    />
                  ) : (
                    <p style={{ ...p, fontStyle: "italic" }}>
                      You will be asked to confirm with Google in a pop-up.
                    </p>
                  )}
                  <div style={{ display: "flex", gap: "0.6rem", flexWrap: "wrap" }}>
                    <button
                      type="button"
                      style={{ ...btn("danger"), opacity: canDelete && !busy ? 1 : 0.5, cursor: canDelete && !busy ? "pointer" : "not-allowed" }}
                      disabled={!canDelete || !!busy}
                      onClick={handleDelete}
                    >
                      {busy === "delete" ? "Deleting..." : "Permanently delete"}
                    </button>
                    <button
                      type="button"
                      style={btn("ghost")}
                      disabled={!!busy}
                      onClick={() => {
                        setConfirming(false);
                        setTyped("");
                        setPassword("");
                        reset();
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>

            <p style={{ fontSize: "0.78rem", color: "#6b7280", margin: "0.4rem 0 0", lineHeight: 1.6 }}>
              Need something else (correction, complaint)? Read our{" "}
              <a href="/privacy" style={{ color: "#7c3aed" }}>Privacy Policy</a> or contact the{" "}
              <a href="/privacy#grievance" style={{ color: "#7c3aed" }}>Grievance Officer</a>.
            </p>
          </>
        )}
      </div>
    </div>
  );
};

export default PrivacyDataModal;
